import { isGroupAdmin } from '@/features/dashboard/power/group/groupDisplay'
import { isLocationScoped, locationMemberMayPeek } from '@/lib/locationScope'
import {
    ADMIN_ROLES,
    PARTNER_ADMIN_ROLES,
    SITE_ADMIN_ROLES,
    SLT_ROLES
} from '@/lib/roles'
import { hasRole } from '@/utils/hasRole'

/**
 * Who may open each kind of peek — the roles the APIs behind it accept
 * (meters: electricity/views.py MeterViewSet, gas GasMeterViewSet, water
 * WaterMeterViewSet; a location peek also reads every meter list; an
 * organisation peek reads the site-admin org endpoint). Anyone else sees the
 * reference as plain text rather than a link to a panel that can't load.
 * Kept apart from registry.jsx so EntityLink can read it without importing
 * the peeks (which import EntityLink).
 */
export const PEEK_ROLES = {
    location: ADMIN_ROLES,
    'electricity-meter': SLT_ROLES,
    'gas-meter': SLT_ROLES,
    'water-meter': ADMIN_ROLES,
    organisation: SITE_ADMIN_ROLES,
    // org/summary.py OrgSummaryView: site admins, partner admins (their
    // partner's orgs) and group admins (their group's members) — group admin is
    // an API designation (administeredGroups), not a role, hence a predicate.
    'org-summary': (user) =>
        hasRole(user, SITE_ADMIN_ROLES) ||
        hasRole(user, PARTNER_ADMIN_ROLES) ||
        isGroupAdmin(user),
    // org/views.py SiteAdminOrgGroupDetailView, SiteAdminPartnerDetailView
    group: SITE_ADMIN_ROLES,
    partner: SITE_ADMIN_ROLES,
    // site_admin UserManagementViewSet (IsSiteAdmin)
    user: SITE_ADMIN_ROLES
}

/**
 * Whether `user` may open a `type` peek. An entry is either the role array
 * the APIs accept (matched with hasRole) or, where access isn't expressible
 * as roles, a predicate `(user) => boolean`.
 */
export function canPeek(user, type, ref = {}) {
    const rule = PEEK_ROLES[type]
    if (!rule || !user) return false
    // A location member peeks only in-scope Locations and meters (`ref` carries the
    // Location id, or the meter's `locationId`); the APIs behind them 404 the rest.
    if (isLocationScoped(user)) return locationMemberMayPeek(user, type, ref)
    return Boolean(
        typeof rule === 'function' ? rule(user) : hasRole(user, rule)
    )
}
